import { createHOC } from 'vue-hoc';
import { get, intersection, groupBy } from 'lodash';
import gql from 'graphql-tag';
import sumBy from 'lodash/sumBy';
import casesService from '~lib/common/providers/casesService';
import withUser from '~lib/common/hoc/withUser';

import assignmentQueries from '~lib/common/queries/Assignment';
import attemptQueries from '../../../graphql/queries/Attempt';

import CasePreview from './CasePreview';

const options = {
  name: 'CasePreviewContainer',
  data() {
    return {
      state: {
        assignments: [],
        attemptsByAssignment: {},
        runFailure: false,
        removeFailure: false,
        isAssignmentActive: undefined,
      },
    };
  },
  async created() {
    this.fetchAll();
  },
  watch: {
    caseConf() {
      this.fetchAll();
    },
  },
  methods: {
    async fetchAll() {
      try {
        Promise.all([
          this.fetchAssignments(),
          this.fetchAttempts(),
        ])
        .then(responses => {
          this.processAssignments(responses[0]);
          this.processAttempts(responses[1]);
        });
      } catch (e) {
        console.log(e);
      }
    },
    async fetchAssignments() {
      if (!this.caseConf) {
        return Promise.resolve();
      }
      return casesService.query({
        query: gql(assignmentQueries.fetch),
        variables: {
          caseId: this.caseConf.id,
        },
        fetchPolicy: 'network-only',
      });
    },
    async fetchAttempts() {
      if (!this.caseConf) {
        return Promise.resolve();
      }
      return casesService.query({
        query: gql(attemptQueries.fetch),
        variables: {
          caseId: this.caseConf.id,
          userId: this.user.user_id,
        },
        fetchPolicy: 'network-only',
      });
    },
    processAssignments(response) {
      const assignments = get(response, 'data.assignments') || [];
      const now = new Date().toJSON();
      const inProgress = assignments.filter(a => a.startTime <= now && now <= a.endTime);
      const pending = assignments.filter(a => a.status === 'PENDING');
      this.state.isAssignmentActive = inProgress.length > 0 || pending.length > 0;
      this.state.assignments = inProgress.filter(a =>
        this.isAssignmentStudent(a, this.user)
      );
    },
    isAssignmentStudent(assignment, user) {
      return assignment.access.owner !== user.user_id
        && (assignment.students.users.includes(user.user_id) ||
            intersection(assignment.students.groups, user.groups).length > 0);
    },
    processAttempts(response) {
      const attempts = get(response, 'data.attempts') || [];
      if (attempts.length) {
        this.state.attemptsByAssignment = groupBy(attempts, 'assignmentId');
      } else {
        this.state.attemptsByAssignment = {};
      }
    },
    async haveRemainingAttempts(assignment) {
      if (assignment) {
        const response = await this.fetchAttempts();
        this.processAttempts(response);
        console.log(this.attemptsUsed(assignment), this.totalAttempts(assignment))
        return (this.attemptsUsed(assignment) < this.totalAttempts(assignment));
      }
      return true;
    },
    attemptsUsed(assignment) {
      const attempts = this.state.attemptsByAssignment[assignment.id];
      return attempts ? attempts.length : 0;
    },
    totalAttempts(assignment) {
      const assignmentAttemptsCount = assignment.attemptsCount;
      const additionalAttemptsCount = assignment.additionalAttempts
        ? sumBy(
            assignment.additionalAttempts.filter(
              attempt =>
                attempt.principalType === 'USER' &&
                attempt.principalId === this.user.id
            ),
            'count'
          )
        : 0;
      const totalAttemptsCount =
        assignmentAttemptsCount + additionalAttemptsCount;
      return totalAttemptsCount;
    },
  },
};

const renderWith = {
  props: {
    caseAssignments() {
      return this.state.assignments;
    },
    attemptsByAssignment() {
      return this.state.attemptsByAssignment;
    },
    runFailure() {
      return this.state.runFailure;
    },
    removeFailure() {
      return this.state.removeFailure;
    },
    isAssignmentActive() {
      return this.state.isAssignmentActive;
    },
    getAttemptsUsed() {
      return this.attemptsUsed;
    },
    getTotalAttempts() {
      return this.totalAttempts;
    },
  },
  listeners: {
    async run(caseConf, orgId, assignment) {
      this.state.runFailure = false;
      if (await this.haveRemainingAttempts(assignment)) {
        this.$emit('run', caseConf, orgId, assignment);
      } else {
        this.state.runFailure = true;
      }
    },
    async remove(caseConf) {
      // Re-fecth asssingments to make sure none are active on this case
      this.state.removeFailure = false;
      const assignments = await this.fetchAssignments();
      this.processAssignments(assignments);
      if (this.state.isAssignmentActive) {
        this.state.removeFailure = true;
      } else {
        this.$emit('remove', caseConf);
      }
    }
  },
};
const CasePreviewContainer = createHOC(CasePreview, options, renderWith);
export default withUser()(CasePreviewContainer);
